import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";

const pages = {
  "/privacy": { file: "/content/privacy.md", title: "Privacy Policy | BizFlowa", description: "BizFlowa Privacy Policy — how we collect, use, and protect your business data." },
  "/terms": { file: "/content/terms.md", title: "Terms of Use | BizFlowa", description: "BizFlowa Terms of Use — rules and conditions for using the platform." },
};

export default function LegalPage() {
  const location = useLocation();
  const config = pages[location.pathname];
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!config) return;
    document.title = config.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", config.description);
    else {
      const tag = document.createElement("meta");
      tag.name = "description";
      tag.content = config.description;
      document.head.appendChild(tag);
    }
    // Try DB first (admin-edited), fallback to static .md
    const slug = location.pathname.replace("/", "");
    const API_URL = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${API_URL}/api/legal/${slug}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data && data.content) {
          setContent(data.content);
          setLoading(false);
        } else {
          // Fallback to static markdown file
          return fetch(config.file).then((r) => r.text()).then((text) => { setContent(text); setLoading(false); });
        }
      })
      .catch(() => {
        fetch(config.file).then((r) => r.text()).then((text) => { setContent(text); setLoading(false); })
          .catch(() => { setContent("# Content Unavailable\n\nPlease try again later."); setLoading(false); });
      });
  }, [config, location.pathname]);

  if (!config) return null;

  return (
    <div className="min-h-screen bg-white" data-testid="legal-page">
      {/* Compact header */}
      <header className="border-b border-[#f1f5f9] bg-white sticky top-0 z-10">
        <div className="max-w-[800px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 group">
            <img src="/bizflowa-logo.png" alt="BizFlowa" className="h-7 w-7 rounded-lg object-contain" />
            <span className="font-outfit text-lg font-bold text-[#0f172a] tracking-tight">BizFlowa</span>
          </Link>
          <Link to="/login" className="flex items-center gap-1.5 text-sm text-[#64748b] hover:text-[#4338ca] transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Login
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[800px] mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#4338ca] border-t-transparent" />
          </div>
        ) : (
          <article className="legal-content" data-testid="legal-content">
            <ReactMarkdown
              components={{
                h1: ({ children }) => <h1 className="font-outfit text-3xl font-bold text-[#0f172a] mb-2 tracking-tight">{children}</h1>,
                h2: ({ children }) => <h2 className="font-outfit text-xl font-semibold text-[#0f172a] mt-10 mb-3 pb-2 border-b border-[#f1f5f9]">{children}</h2>,
                h3: ({ children }) => <h3 className="font-outfit text-base font-semibold text-[#0f172a] mt-6 mb-2">{children}</h3>,
                p: ({ children }) => <p className="text-[15px] leading-relaxed text-[#475569] mb-4">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1.5">{children}</ul>,
                li: ({ children }) => <li className="text-[15px] leading-relaxed text-[#475569]">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-[#0f172a]">{children}</strong>,
                em: ({ children }) => <em className="text-[#64748b] italic">{children}</em>,
                hr: () => <hr className="my-8 border-[#e2e8f0]" />,
                a: ({ href, children }) => <a href={href} className="text-[#4338ca] hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
              }}
            >
              {content}
            </ReactMarkdown>
          </article>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#f1f5f9] py-6">
        <div className="max-w-[800px] mx-auto px-6 flex items-center justify-between text-xs text-[#94a3b8]">
          <span>Bizflowa Private Limited</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-[#4338ca] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#4338ca] transition-colors">Terms of Use</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
